(() => {
  'use strict';
  if (customElements.get('aisekar-launch-announcement')) return;

  class AisekarLaunchAnnouncement extends HTMLElement {
    connectedCallback() {
      this.bar = this.querySelector('.launch-bar');
      this.countdown = this.querySelector('[data-countdown-panel]');
      this.live = this.querySelector('[data-live-panel]');
      this.clock = this.querySelector('.launch-clock');
      this.status = this.querySelector('[data-launch-status]');
      this.preview = window.Shopify?.designMode ? this.dataset.preview : 'auto';
      this.deadline = this.parseDeadline(this.dataset.deadline);
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.controller = new AbortController();
      this.update = this.update.bind(this);
      this.measure = this.measure.bind(this);
      this.updateContent = this.updateContent.bind(this);
      if (!Number.isFinite(this.deadline)) {
        this.querySelector('[data-launch-error]')?.removeAttribute('hidden');
        // Invalid settings never create a fabricated ticking deadline.
        this.clock.hidden = true;
      }
      this.update();
      this.updateContent();
      this.measure();
      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(this.measure);
        this.observer.observe(this.bar);
      }
      window.addEventListener('resize', this.measure, { passive: true, signal: this.controller.signal });
      window.addEventListener('pageshow', this.update, { signal: this.controller.signal });
      document.addEventListener('visibilitychange', this.update, { signal: this.controller.signal });
      document.addEventListener('shopify:section:load', this.updateContent, { signal: this.controller.signal });
      if (Number.isFinite(this.deadline)) this.interval = window.setInterval(this.update, 1000);
    }

    parseDeadline(value) {
      const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):00\+08:00$/.exec(value || '');
      if (!match) return NaN;
      const [, year, month, day, hour, minute] = match.map(Number);
      if (year < 2026 || month < 1 || month > 12 || day < 1 || hour > 23 || minute > 59) return NaN;
      const date = new Date(Date.UTC(year, month - 1, day, hour, minute));
      if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return NaN;
      return date.getTime() - 8 * 60 * 60 * 1000;
    }

    update() {
      const remaining = this.deadline - Date.now();
      const state = this.preview === 'live' || (this.preview !== 'countdown' && remaining <= 0) ? 'live' : 'countdown';
      const changed = this.dataset.state !== state;
      const moveFocus = changed && state === 'live' && this.countdown.contains(document.activeElement);
      this.dataset.state = state;
      this.countdown.hidden = state !== 'countdown';
      this.live.hidden = state !== 'live';
      document.documentElement.dataset.aisekarLaunch = state;
      if (changed) this.updateContent();
      if (state === 'countdown' && Number.isFinite(remaining)) {
        // Round up to a whole second; the final second remains visible until launch.
        const total = Math.max(0, Math.ceil(remaining / 1000));
        const values = { days: Math.floor(total / 86400), hours: Math.floor(total % 86400 / 3600), minutes: Math.floor(total % 3600 / 60), seconds: total % 60 };
        Object.entries(values).forEach(([unit, value]) => {
          const element = this.querySelector(`[data-unit="${unit}"]`);
          const next = String(value).padStart(2, '0');
          if (element.textContent === next) return;
          element.textContent = next;
          if (!this.motion.matches && !document.hidden) {
            element.classList.remove('is-turning');
            void element.offsetWidth;
            element.classList.add('is-turning');
          }
        });
        this.clock.setAttribute('aria-label', `${values.days} days, ${values.hours} hours, ${values.minutes} minutes, ${values.seconds} seconds left to reserve`);
      }
      if (changed) {
        this.status.textContent = state === 'live' ? 'Aisekar S1 is now live on Kickstarter.' : '';
        this.measure();
        if (moveFocus) this.live.querySelector('a')?.focus({ preventScroll: true });
      }
    }

    updateContent() {
      const state = this.dataset.state;
      const active = document.activeElement;
      let replacement;
      document.querySelectorAll('[data-launch-phase]').forEach(panel => {
        const visible = panel.dataset.launchPhase === state;
        if (!visible && panel.contains(active)) {
          replacement = panel.parentElement.querySelector('[data-launch-phase="' + state + '"] a');
        }
        panel.hidden = !visible;
        panel.inert = !visible;
      });
      document.querySelectorAll('[data-campaign-cta]').forEach(link => {
        if (this.dataset.campaignUrl) link.href = this.dataset.campaignUrl;
        link.hidden = !this.dataset.campaignUrl || (link.dataset.launchPhase && link.dataset.launchPhase !== state);
      });
      replacement?.focus({ preventScroll: true });
      window.dispatchEvent(new CustomEvent('aisekar:launch-state-change', { detail: { state } }));
    }

    measure() {
      const height = Math.ceil(this.bar.getBoundingClientRect().height);
      if (!height || height === this.height) return;
      this.height = height;
      document.documentElement.style.setProperty('--announcement-height', `${height}px`);
      window.dispatchEvent(new CustomEvent('aisekar:announcement-resize'));
    }

    disconnectedCallback() {
      clearInterval(this.interval);
      this.observer?.disconnect();
      this.controller?.abort();
      document.documentElement.style.removeProperty('--announcement-height');
      delete document.documentElement.dataset.aisekarLaunch;
      window.dispatchEvent(new CustomEvent('aisekar:announcement-resize'));
    }
  }
  customElements.define('aisekar-launch-announcement', AisekarLaunchAnnouncement);
})();
